"use client";

import * as React from "react";
import { Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/shadcn/button";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Popover, PopoverTrigger } from "@/components/ui/shadcn/popover";
import { TwoStageTooltip } from "@/components/ui/common/TwoStageTooltip";
import { NotificationDropdown } from "./NotificationDropdown";
import type { Notification } from "@/types/notifications";

export interface NotificationBellProps {
  notifications: Notification[];
  showRead: boolean;
  onToggleShowRead: (show: boolean) => void;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onClearToasts: () => void;
  onNotificationClick?: (notification: Notification) => void;
  isLoading?: boolean;
}

export function NotificationBell({
  notifications,
  showRead,
  onToggleShowRead,
  onMarkAsRead,
  onMarkAllAsRead,
  onClearToasts,
  onNotificationClick,
  isLoading = false,
}: NotificationBellProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  // Calculate unread count (only news, not toasts)
  const unreadCount = React.useMemo(() => {
    return notifications.filter((n) => n.readAt === null && n.kind === "news").length;
  }, [notifications]);

  const hasUnread = unreadCount > 0;

  // Format unread count (show 99+ for large numbers)
  const unreadLabel = unreadCount > 99 ? "99+" : unreadCount.toString();

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <TwoStageTooltip label="Notifications" hint="News & updates" side="bottom" disabled={isOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "relative h-9 w-9 text-muted-foreground hover:text-foreground",
              hasUnread && "text-foreground"
            )}
            aria-label={`Notifications ${hasUnread ? `(${unreadCount} unread)` : ""}`}
          >
            <Bell className="h-5 w-5" />

            {/* Unread indicator badge */}
            {hasUnread && (
              <span
                className={cn(
                  "absolute right-0.5 top-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white shadow-lg",
                  "animate-in fade-in zoom-in duration-200"
                )}
                aria-hidden="true"
              >
                {unreadLabel}
              </span>
            )}

            {/* Subtle slow pulse animation ring for new notifications */}
            {hasUnread && (
              <span className="absolute inset-0 animate-[ping_3s_ease-in-out_infinite] rounded-full bg-red-500 opacity-10" />
            )}
          </Button>
        </PopoverTrigger>
      </TwoStageTooltip>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="end"
          side="bottom"
          sideOffset={8}
          className="z-50 w-screen sm:w-auto outline-none data-[state=open]:animate-notification-in data-[state=closed]:animate-notification-out"
        >
          <NotificationDropdown
            notifications={notifications}
            showRead={showRead}
            onToggleShowRead={onToggleShowRead}
            onMarkAsRead={onMarkAsRead}
            onMarkAllAsRead={onMarkAllAsRead}
            onClearToasts={onClearToasts}
            onNotificationClick={onNotificationClick}
            onClose={() => setIsOpen(false)}
            isLoading={isLoading}
          />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </Popover>
  );
}
